/** Packing Area & stock out — paid orders waiting to be packed (incl. manual stock-out orders), packed orders waiting for the courier, and what went out recently. */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Boxes, Loader2, PackageCheck, PackagePlus, RefreshCw, Search, Truck } from 'lucide-react';
import { toast } from 'sonner';
import AuthContext from '@/contexts/AuthContext';
import { PackCard } from '@/components/packing/PackCard';
import { PackSheet } from '@/components/packing/PackSheet';
import { DispatchDialog } from '@/components/packing/DispatchDialog';
import { ManualOrderDialog } from '@/components/packing/ManualOrderDialog';
import { packApi } from '@/components/packing/shared';

const TABS = [
  { key: 'to_pack', label: 'To pack', icon: Boxes },
  { key: 'packed', label: 'Packed', icon: PackageCheck },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
];

export default function PackingArea() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isAdmin, isManager } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('to_pack');
  const [q, setQ] = useState('');
  const [packing, setPacking] = useState(null);
  const [dispatching, setDispatching] = useState(null);
  const [adding, setAdding] = useState(params.get('new') === '1');

  const load = useCallback(async () => {
    try { setData(await packApi('GET', '/queue')); } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const list = useMemo(() => {
    const rows = data?.[tab] || [];
    const s = q.trim().toLowerCase();
    return s ? rows.filter((o) => [o.reference, o.customer, o.company_name, o.installer_name].some((v) => (v || '').toLowerCase().includes(s))) : rows;
  }, [data, tab, q]);
  const awaiting = (data?.to_pack || []).filter((o) => o.status === 'awaiting_approval').length;

  const approve = async (o) => {
    try { await packApi('POST', `/manual/${o.id}/approve`); toast.success(`${o.reference} approved — stock deducted, ready to pack`); load(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Remove ${o.reference}?${o.manual?.stock ? ' Stock already deducted will NOT be put back automatically.' : ''}`)) return;
    try { await packApi('DELETE', `/manual/${o.id}`); toast.success(`${o.reference} removed`); load(); } catch (e) { toast.error(e.message); }
  };
  const closeAdd = () => { setAdding(false); if (params.get('new')) { params.delete('new'); setParams(params, { replace: true }); } };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="packing-area">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #1f2937, #0f766e)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard" data-testid="packing-back"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2"><Boxes className="w-5 h-5" /> Packing &amp; Stock Out</h1>
            <p className="text-white/70 text-xs">Tick it, photograph it, mark it packed — stock and your timesheet update automatically.</p>
          </div>
          <button type="button" onClick={() => setAdding(true)} className="h-10 px-3 rounded-lg bg-amber-500 text-amber-950 font-bold text-sm inline-flex items-center gap-1.5 shrink-0" data-testid="packing-add-manual"><PackagePlus className="w-4 h-4" /> <span className="hidden sm:inline">Add order</span></button>
          <button type="button" onClick={load} className="p-2 hover:bg-white/10 rounded-lg" aria-label="Refresh" data-testid="packing-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-1.5" role="tablist">
          {TABS.map((t) => {
            const n = data?.[t.key]?.length || 0;
            return (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`packing-tab-${t.key}`}
                className={`flex-1 rounded-lg px-2 py-2 text-xs sm:text-sm font-bold inline-flex items-center justify-center gap-1.5 ${tab === t.key ? 'bg-white text-gray-900' : 'bg-white/10 text-white/80'}`}>
                <t.icon className="w-4 h-4 hidden sm:inline" /> {t.label} <span className={`ml-0.5 rounded-full px-1.5 text-[10px] ${tab === t.key ? 'bg-gray-900 text-white' : 'bg-white/20'}`} data-testid={`packing-count-${t.key}`}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference or customer…" className="w-full h-10 pl-9 pr-3 rounded-xl border bg-white text-sm" data-testid="packing-search" />
        </div>
        {tab === 'to_pack' && awaiting > 0 && (
          <div className="text-xs font-semibold text-violet-800 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2" data-testid="packing-awaiting-banner">
            {awaiting} manual order{awaiting === 1 ? '' : 's'} awaiting manager approval{isAdmin || isManager ? ' — approve below to deduct stock and release for packing.' : '.'}
          </div>
        )}
        {!data && <div className="text-center text-gray-400 py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}
        {data && list.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-12 border-2 border-dashed rounded-xl bg-white" data-testid="packing-empty">
            {tab === 'to_pack' ? 'Nothing waiting to be packed — all paid orders are done.' : tab === 'packed' ? 'No packed orders waiting for the courier.' : 'Nothing dispatched in the last 14 days.'}
          </div>
        )}
        {list.map((o) => (
          <PackCard key={`${o.product}-${o.id}`} order={o} onPack={tab === 'to_pack' ? setPacking : undefined} onDispatch={tab === 'packed' ? setDispatching : undefined}
            onApprove={approve} onRemove={tab === 'to_pack' ? remove : undefined} canApprove={isAdmin || isManager} />
        ))}
      </div>

      {adding && <ManualOrderDialog onClose={closeAdd} onDone={() => { closeAdd(); setTab('to_pack'); load(); }} />}
      {packing && <PackSheet order={packing} onClose={() => { setPacking(null); load(); }} onDone={() => { setPacking(null); setTab('packed'); load(); }} />}
      {dispatching && <DispatchDialog order={dispatching} onClose={() => setDispatching(null)} onDone={() => { setDispatching(null); setTab('dispatched'); load(); }} />}
    </div>
  );
}
